import { Button, Combobox, ComboboxCountryItem, ComboboxCountryValue, Input, lucideIcons } from '@round/creatorbase-ui';
import { OperatorFilter } from '@round/api';
import { countryOptions } from 'utility/constants';
import { AudienceLocationCondition } from '../../utils/filters';
import styles from './AudienceLocationFilter.module.css';

const { Plus, X } = lucideIcons;

type Props = {
    value: OperatorFilter<AudienceLocationCondition> | undefined;
    onChange: (value: OperatorFilter<AudienceLocationCondition> | undefined) => void;
};

export function AudienceLocationFilter({ value, onChange }: Props) {
    const conditions = value?.conditions ?? [];

    const updateConditions = (next: AudienceLocationCondition[]) => {
        if (!next.length) {
            onChange(undefined);
            return;
        }

        onChange({ ...value, conditions: next });
    };

    const addCondition = () => {
        updateConditions([...conditions, { countryCode: '', minPercentage: undefined }]);
    };

    const removeCondition = (index: number) => {
        updateConditions(conditions.filter((_, i) => i !== index));
    };

    const updateCondition = (index: number, patch: Partial<AudienceLocationCondition>) => {
        updateConditions(conditions.map((c, i) => (i === index ? { ...c, ...patch } : c)));
    };

    return (
        <div className={styles.container}>
            {conditions.map((condition, index) => {
                const selectedItem = countryOptions.find((o) => o.value === condition.countryCode) ?? null;
                return (
                    <div key={index} className={styles.conditionRow}>
                        <div className={styles.comboboxWrap}>
                            <Combobox
                                items={countryOptions}
                                isItemEqualToValue={(a, b) => a.value === b.value}
                                value={selectedItem}
                                onValueChange={(item) => updateCondition(index, { countryCode: item?.value ?? '' })}
                                placeholder="Search country…"
                                isClearable
                                noItemsFound="No countries found"
                                renderItem={(item) => (
                                    <ComboboxCountryItem item={{ ...item, countryCode: item.value.toLowerCase() }} />
                                )}
                                renderValue={(item) => (
                                    <ComboboxCountryValue item={{ ...item, countryCode: item.value.toLowerCase() }} />
                                )}
                                multiple={false}
                            />
                        </div>

                        <Input
                            type="number"
                            placeholder="%"
                            className={styles.minInput}
                            min={0}
                            max={100}
                            value={condition.minPercentage || ''}
                            onChange={(e) =>
                                updateCondition(index, { minPercentage: Number(e.target.value) || undefined })
                            }
                            debounce={300}
                        />

                        <Button variant="ghost" className={styles.removeButton} onClick={() => removeCondition(index)}>
                            <X />
                        </Button>
                    </div>
                );
            })}

            <Button variant="outline" className={styles.addButton} onClick={addCondition}>
                <Plus />
                Add Location
            </Button>
        </div>
    );
}
